import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Conversa, ConversaContexto, ConversaDono, ConversaEscopo, ConversaMidia, ConversaSituacao } from '../interfaces/conversa';
import { ConversaMensagem } from '../interfaces/conversa-mensagem';
import { PageResult } from '../interfaces/page-result';
import { JwtAuthService } from './auth/jwt-auth.service';

/**
 * Caixa de entrada do WhatsApp.
 *
 * Fala com `/api/conversa`, que lê do banco — separado de `WhatsappService`, que chama a Evolution
 * para enviar. Por isso a lista continua abrindo mesmo com o WhatsApp fora do ar.
 */
@Injectable({ providedIn: 'root' })
export class ConversaService {

  readonly path = '/conversa';

  constructor(private http: HttpClient, private jwtAuthService: JwtAuthService) { }

  private get url(): string {
    return this.jwtAuthService.apiUrl + this.path;
  }

  /**
   * Lista da caixa de entrada, mais recente primeiro.
   *
   * A busca manda no lugar da situação: quem procura um nome quer achar a conversa inclusive se ela
   * já foi resolvida.
   */
  getPage(page: number, size: number, situacao?: ConversaSituacao, busca?: string,
          todas?: boolean, dono?: ConversaDono): Observable<PageResult<Conversa>> {
    let params = new HttpParams().set('page', String(page)).set('size', String(size));
    if (busca) {
      params = params.set('busca', busca);
    } else if (situacao) {
      params = params.set('situacao', situacao);
    }
    // Visão de supervisão: o backend só atende a quem administra, e ignora para os demais.
    if (todas) {
      params = params.set('todas', 'true');
    }
    // O login de "minhas" sai da sessão no backend; aqui só vai o recorte pedido.
    if (dono) {
      params = params.set('dono', dono);
    }
    return this.http.get<PageResult<Conversa>>(`${this.url}/page/get`, { params });
  }

  /**
   * Quais números esta sessão enxerga.
   *
   * A tela usa para explicar uma caixa vazia ("você não atende por nenhum número") e para mostrar de
   * qual número é cada conversa quando há mais de um — sem isso, duas linhas do mesmo contato em
   * números diferentes parecem duplicidade sem explicação.
   */
  getEscopo(): Observable<ConversaEscopo> {
    return this.http.get<ConversaEscopo>(`${this.url}/escopo`);
  }

  /**
   * Uma conversa pelo id.
   *
   * A tela usa ao voltar da ficha do cliente: a lista recomeça da primeira página, e a conversa que
   * estava aberta pode não estar nela.
   */
  getConversa(conversaId: number): Observable<Conversa> {
    return this.http.get<Conversa>(`${this.url}/${conversaId}`);
  }

  /** Total de não lidas: o número que aparece no menu. */
  getNaoLidas(): Observable<{ total: number }> {
    return this.http.get<{ total: number }>(`${this.url}/nao-lidas`);
  }

  getMensagens(conversaId: number, page: number, size: number): Observable<PageResult<ConversaMensagem>> {
    const params = new HttpParams().set('page', String(page)).set('size', String(size));
    return this.http.get<PageResult<ConversaMensagem>>(`${this.url}/${conversaId}/mensagens/page/get`, { params });
  }

  /** Mensagens por telefone: abre a conversa a partir do cartão do funil ou da ficha do cliente. */
  getMensagensPorContato(contato: string, page: number, size: number): Observable<PageResult<ConversaMensagem>> {
    const params = new HttpParams()
      .set('contato', contato).set('page', String(page)).set('size', String(size));
    return this.http.get<PageResult<ConversaMensagem>>(`${this.url}/mensagens/page/get`, { params });
  }

  /**
   * Contexto da conversa: cliente, cartões no funil, títulos em aberto e últimas compras.
   *
   * Chamada à parte da lista de propósito: custa consultas no financeiro e no funil, e pagar isso
   * por linha da caixa de entrada seriam dezenas de consultas para mostrar o de uma conversa só.
   */
  getContexto(conversaId: number): Observable<ConversaContexto> {
    return this.http.get<ConversaContexto>(`${this.url}/${conversaId}/contexto`);
  }

  /**
   * URL assinada do arquivo de uma mensagem de mídia.
   *
   * A primeira chamada de cada mídia pode levar alguns segundos: é quando a API busca o arquivo na
   * Evolution e guarda. Depois sai do armazenamento, na hora.
   */
  getMidia(mensagemId: number): Observable<ConversaMidia> {
    return this.http.get<ConversaMidia>(`${this.url}/mensagem/${mensagemId}/midia`);
  }

  /**
   * Liga o arquivo de uma mensagem ao cadastro, lançamento ou agenda.
   *
   * O arquivo já está no S3 desde que alguém o abriu na conversa: aqui só nasce o vínculo, sem
   * subir nada de novo. O mesmo arquivo pode ser ligado a mais de um nível.
   */
  anexarMensagem(mensagemId: number, destino: 'CADASTRO' | 'LANCAMENTO' | 'AGENDA',
                 alvoId: number | string, observacao?: string): Observable<any> {
    return this.http.post<any>(`${this.url}/mensagem/${mensagemId}/anexar`,
      { destino, alvoId, observacao });
  }

  /**
   * Traz as conversas que já existem na instância da Evolution.
   *
   * É a carga inicial: o webhook só conhece o que chegou depois de ele existir. O que vem é o que a
   * Evolution guardou — instância recém-pareada traz pouco, porque o histórico mora no aparelho.
   */
  importar(): Observable<{ conversas: number; removidas?: number }> {
    return this.http.post<{ conversas: number; removidas?: number }>(`${this.url}/importar`, null);
  }

  /**
   * Monta a caixa de entrada a partir das mensagens já gravadas.
   *
   * Não importa o histórico do WhatsApp: reconstrói do que a API registrou pelo webhook. Serve para
   * a primeira carga, depois de publicar a funcionalidade.
   */
  reconstruir(): Observable<{ conversas: number }> {
    return this.http.post<{ conversas: number }>(`${this.url}/reconstruir`, null);
  }

  marcarLida(conversaId: number): Observable<Conversa> {
    return this.http.post<Conversa>(`${this.url}/${conversaId}/lida`, null);
  }

  mudarSituacao(conversaId: number, valor: ConversaSituacao): Observable<Conversa> {
    const params = new HttpParams().set('valor', valor);
    return this.http.put<Conversa>(`${this.url}/${conversaId}/situacao`, null, { params });
  }

  /** Assume o atendimento em nome de quem está logado, ou libera com `assumir = false`. */
  assumir(conversaId: number, assumir = true): Observable<Conversa> {
    const params = new HttpParams().set('assumir', String(assumir));
    return this.http.put<Conversa>(`${this.url}/${conversaId}/responsavel`, null, { params });
  }
}
